import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { SystemApp } from '../constans/SystemConfigurations';
import { ApiUrl } from '../apiurl.models';
import { AuthSessionService } from '../core/auth/auth-session.service';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Models — mirrors notification/models.py::Notification exactly ────────

export interface AppNotification {
  id: number;
  user: number;
  message: string;
  subject: string | null;
  /** Navigation payload contract — see notification/models.py docstring.
   * { version, action, type, route, params, url, deeplink?, priority?, source? } */
  data: {
    version?: number;
    action?: string;
    type?: string;
    route?: string;
    params?: Record<string, any>;
    url?: string;
    deeplink?: string;
    source?: string;
  } | null;
  link: string | null;
  timestamp: string;
  is_read: boolean;
  flag: boolean;
  image: string | null;
  notification_type: string | null;
}

export interface NotificationWsEvent {
  type: 'new_notification';
  unread_count: number;
}

// ─── Service ────────────────────────────────────────────────────────────────

const BASE = SystemApp.base_url_api + '/notification-api/notifications';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private socket$: WebSocketSubject<NotificationWsEvent> | null = null;
  private reconnectTimeout: any;
  private connecting = false;
  private failures = 0;

  /** Emits every time the server pushes a new notification (SCRUM — bell). */
  public updates$ = new Subject<NotificationWsEvent>();

  constructor(private http: HttpClient, private apiUrl: ApiUrl, private authSession: AuthSessionService) {}

  private get headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: this.apiUrl.getJWTToken() });
  }

  getNotifications(params: { page?: number; page_size?: number } = {}): Observable<AppNotification[]> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page);
    if (params.page_size) httpParams = httpParams.set('page_size', params.page_size);
    return this.http
      .get<PaginatedResponse<AppNotification>>(`${BASE}/`, { headers: this.headers, params: httpParams })
      .pipe(map((res) => res.results));
  }

  getUnreadCount(): Observable<{ unread_messages_count: number }> {
    return this.http.get<{ unread_messages_count: number }>(`${BASE}/app_info/`, { headers: this.headers });
  }

  markRead(id: number): Observable<{ success: boolean; unread_count: number }> {
    return this.http.post<{ success: boolean; unread_count: number }>(
      `${BASE}/mark_read/`, { id }, { headers: this.headers },
    );
  }

  markAllRead(unread: AppNotification[]): Observable<any>[] {
    return unread.filter((n) => !n.is_read).map((n) => this.markRead(n.id));
  }

  // ── Live updates over ws/notifications/ ──────────────────────────────────

  // WebSocket handshakes never pass through AuthInterceptor's 401-refresh,
  // and the access token lives 15 minutes. Reconnecting with the stored token
  // after it expired was rejected (invalid_token -> 403) every 3 s forever
  // (prod, 2026-11-01, ~700 refused handshakes/hour). Same fix as
  // DashboardChatWebsocketService: refresh first when the token is expired or
  // about to expire, and back off after repeated failures.
  private static expSeconds(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof json.exp === 'number' ? json.exp : null;
    } catch {
      return null;
    }
  }

  private async freshToken(): Promise<string> {
    const current = this.apiUrl.getJWTTokenKey();
    const exp = current ? NotificationService.expSeconds(current) : null;
    if (exp === null || exp * 1000 <= Date.now() + 30000) {
      try {
        await this.authSession.refresh().toPromise();
      } catch {
        // revoked session - connect with whatever is left (likely nothing)
      }
    }
    return this.apiUrl.getJWTTokenKey() || '';
  }

  async connect(): Promise<void> {
    if (this.socket$ || this.connecting) return;
    this.connecting = true;
    let token = '';
    try {
      token = await this.freshToken();
    } finally {
      this.connecting = false;
    }
    if (!token) return; // signed out - stay disconnected
    if (this.socket$) return;
    // Token in the Sec-WebSocket-Protocol header, not the URL (2026-10-24).
    const url = `${SystemApp.web_socket}notifications/`;

    this.socket$ = webSocket({
      url,
      protocol: ['bearer', token],
      deserializer: (event) => JSON.parse(event.data),
      openObserver: { next: () => { this.failures = 0; } },
    });

    this.socket$.subscribe({
      next: (event) => this.updates$.next(event),
      error: () => this.scheduleReconnect(),
      complete: () => this.scheduleReconnect(),
    });
  }

  private scheduleReconnect(): void {
    this.socket$ = null;
    if (this.reconnectTimeout) return;
    this.failures += 1;
    // 3 s, then 6, 12 ... up to 60 s while the server keeps refusing.
    const delay = Math.min(60000, 3000 * Math.pow(2, Math.max(0, this.failures - 1)));
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.socket$?.complete();
    this.socket$ = null;
  }
}
